import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useGetAdminTaskQuery,
  useRunTaskMutation,
  useStopTaskMutation,
  useSetTaskPendingMutation,
  useDeleteTaskMutation,
  useUpdateAdminTaskMutation,
  useSubmitTask2FACodeMutation,
  useGetTask2FAStatusQuery,
  useGetBackendUserBotConnectionsQuery,
  useLazyGetTaskStatusLogsQuery,
  useLazyGetTaskFileLogsQuery,
  useLazyGetTaskAllLogsQuery,
  useLazyGetTaskEmployeeStatsQuery,
  useLazyGetTaskCloudWatchLogsQuery,
} from "features/adminUsers/adminUsers";
import { useRefreshTokenMutation } from "features/auth/auth";
import { useAppSelector } from "features/store";
import { taskLogsWs, TaskLogsWsMessage, TaskLogEvent } from "features/ws/taskLogsWs";

type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

const RUNNING_STATUSES = ["ECS_CONTAINER_STARTED", "PROCESSING"];

function taskRunStatusChipColor(status: string): ChipColor {
  switch ((status || "").toUpperCase()) {
    case "PROCESSING":
    case "ECS_CONTAINER_STARTED":
      return "primary";
    case "FINISHED":
      return "success";
    case "PENDING":
      return "default";
    case "ERROR":
    case "CRITICAL_ERROR":
    case "STOPPED":
      return "error";
    default:
      return "default";
  }
}

function botWorkStatusChipColor(status: string): ChipColor {
  switch ((status || "").toUpperCase()) {
    case "2FA":
      return "warning";
    case "LOGGED_IN":
      return "info";
    case "PROCESSING":
      return "primary";
    case "FINISHED":
      return "success";
    case "ERROR":
    case "STOPPED":
      return "error";
    default:
      return "default";
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { data?: { message?: string } })?.data;
  return data?.message || fallback;
}

function formatTs(ms?: number | null): string {
  if (!ms) return "";
  try {
    return new Date(ms).toISOString().replace("T", " ").replace("Z", "");
  } catch {
    return "";
  }
}

function humanizeKind(kind?: string | null): string {
  const v = (kind || "").trim();
  if (!v) return "";
  return v
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Workspace-style label tile, matching the Major Job workspace header cards.
function StripItem({
  title,
  value,
  avatar,
  valueColor,
  onClick,
  tooltip,
}: {
  title: string;
  value: string;
  avatar?: boolean;
  valueColor?: string;
  onClick?: () => void;
  tooltip?: string;
}) {
  const tile = (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "flex-start",
        gap: avatar ? 1 : 0,
        px: 1.25,
        py: 0.6,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "grey.50"),
        border: 1,
        borderColor: "divider",
        boxShadow: (theme) =>
          theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s",
        "&:hover": onClick ? { borderColor: "primary.main" } : undefined,
      }}
    >
      {avatar ? (
        <Box sx={{ mt: 0.2 }}>
          <PersonOutlineIcon color="primary" sx={{ fontSize: 21, opacity: 0.9 }} />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1.1, letterSpacing: 0.06, fontWeight: 600, display: "block" }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ lineHeight: 1.35, color: valueColor, wordBreak: "break-word" }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{tile}</Tooltip> : tile;
}

// Light, AWS-CloudWatch-like log surface. Height is controlled by the caller so
// the box can flex-fill the panel instead of forcing a fixed scroll region.
const LOG_BOX_SX = {
  fontFamily: "monospace",
  fontSize: "0.78rem",
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  m: 0,
  p: 1.5,
  bgcolor: "#ffffff",
  color: "#1a1a1a",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  overflow: "auto",
};

// Fixed, generous log viewport (AWS-CloudWatch-like). The box never grows past
// this — overflow scrolls inside it instead of stretching the workspace.
const LOG_BOX_HEIGHT = { xs: 320, sm: 400, md: 460 };

// ---------------------------------------------------------------------------
// Live logs tab
// ---------------------------------------------------------------------------

type HourBucket = { start: number; end: number; label: string };

function buildHourBuckets(first?: number | null, last?: number | null): HourBucket[] {
  if (!first || !last) return [];
  const HOUR = 3600_000;
  const startHour = Math.floor(first / HOUR) * HOUR;
  const endHour = Math.floor(last / HOUR) * HOUR;
  const buckets: HourBucket[] = [];
  for (let t = startHour; t <= endHour; t += HOUR) {
    buckets.push({
      start: t,
      end: t + HOUR,
      label: `${formatTs(t)} — ${formatTs(t + HOUR)}`,
    });
  }
  return buckets.reverse(); // newest first
}

function LiveLogsTab({
  taskId,
  liveAvailable,
  isLocalRun,
  taskTerminal,
}: {
  taskId: number;
  liveAvailable: boolean;
  isLocalRun: boolean;
  taskTerminal: boolean;
}) {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [refreshToken] = useRefreshTokenMutation();

  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [paused, setPaused] = useState(false);
  const [liveLines, setLiveLines] = useState<string[]>([]);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const liveBoxRef = useRef<HTMLPreElement | null>(null);

  // History (lazy by hour)
  const [fetchCw] = useLazyGetTaskCloudWatchLogsQuery();
  const [buckets, setBuckets] = useState<HourBucket[]>([]);
  const [openHour, setOpenHour] = useState<number | null>(null);
  const [hourEvents, setHourEvents] = useState<TaskLogEvent[]>([]);
  const [hourNextToken, setHourNextToken] = useState<string | null>(null);
  const [hourLoading, setHourLoading] = useState(false);

  // --- WebSocket live tail ---
  useEffect(() => {
    if (!liveAvailable) return;
    taskLogsWs.setTokenProvider(() => localStorage.getItem("accessToken"));
    const unStatus = taskLogsWs.subscribeStatus(setConnected);
    const unAuth = taskLogsWs.subscribeAuthRejected(() => {
      refreshToken()
        .unwrap()
        .then((result) => {
          if (result?.access) taskLogsWs.connect(result.access, taskId);
        })
        .catch(() => undefined);
    });
    const unMsg = taskLogsWs.subscribe((msg: TaskLogsWsMessage) => {
      if (msg.type === "logs") {
        if (pausedRef.current) return;
        const lines = msg.events.map(
          (e) => `${e.timestamp ? formatTs(e.timestamp) + "  " : ""}${e.message}`,
        );
        setLiveLines((prev) => {
          const next = [...prev, ...lines];
          // Cap in-memory live buffer to avoid unbounded growth.
          return next.length > 5000 ? next.slice(next.length - 5000) : next;
        });
      } else if (msg.type === "end") {
        setEnded(msg.status || "finished");
      } else if (msg.type === "unavailable") {
        setUnavailable(true);
      }
    });

    const token = accessToken || localStorage.getItem("accessToken");
    if (token) taskLogsWs.connect(token, taskId);

    return () => {
      unStatus();
      unAuth();
      unMsg();
      taskLogsWs.disconnect();
      taskLogsWs.setTokenProvider(null);
    };
  }, [taskId, liveAvailable]);

  // Auto-scroll live box
  useEffect(() => {
    if (paused) return;
    const el = liveBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveLines, paused]);

  // Load history range once
  useEffect(() => {
    if (!liveAvailable) return;
    fetchCw({ taskId })
      .unwrap()
      .then((res) => {
        if (!res.local && res.range) {
          setBuckets(buildHourBuckets(res.range.first, res.range.last));
        }
      })
      .catch(() => undefined);
  }, [taskId, liveAvailable, fetchCw]);

  const loadHour = async (bucket: HourBucket, append = false) => {
    setHourLoading(true);
    try {
      const res = await fetchCw({
        taskId,
        start: bucket.start,
        end: bucket.end,
        nextToken: append ? hourNextToken ?? undefined : undefined,
      }).unwrap();
      const events = res.events ?? [];
      setHourEvents((prev) => (append ? [...prev, ...events] : events));
      setHourNextToken(res.next_token ?? null);
    } finally {
      setHourLoading(false);
    }
  };

  const toggleHour = (bucket: HourBucket) => {
    if (openHour === bucket.start) {
      setOpenHour(null);
      setHourEvents([]);
      setHourNextToken(null);
      return;
    }
    setOpenHour(bucket.start);
    setHourEvents([]);
    setHourNextToken(null);
    void loadHour(bucket, false);
  };

  if (!liveAvailable || unavailable) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {isLocalRun
          ? "This bot ran locally — live CloudWatch logs are unavailable. Status/file logs and task controls remain available."
          : "Live CloudWatch logs are unavailable for this task. Status/file logs and task controls remain available."}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Live tail</Typography>
          <Chip
            size="small"
            label={connected ? "Connected" : "Disconnected"}
            color={connected ? "success" : "default"}
            variant="outlined"
          />
          {ended && (
            <Chip size="small" label={`Ended: ${ended}`} color="info" variant="outlined" />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => setPaused((p) => !p)}>
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button size="small" onClick={() => setLiveLines([])}>
            Clear
          </Button>
        </Stack>
        {taskTerminal && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This task has finished — the live tail won&apos;t update. Browse History
            (hourly) below for the full log.
          </Typography>
        )}
        <Box component="pre" ref={liveBoxRef} sx={{ ...LOG_BOX_SX, height: LOG_BOX_HEIGHT }}>
          {liveLines.length
            ? liveLines.join("\n")
            : taskTerminal
              ? "(task finished — see History below)"
              : "(waiting for new lines...)"}
        </Box>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          History (hourly)
        </Typography>
        {buckets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            History unavailable (log stream not created yet).
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {buckets.map((bucket) => (
              <Box key={bucket.start}>
                <Button
                  size="small"
                  fullWidth
                  sx={{ justifyContent: "flex-start", textTransform: "none" }}
                  onClick={() => toggleHour(bucket)}
                >
                  {openHour === bucket.start ? "▾" : "▸"}&nbsp;{bucket.label}
                </Button>
                {openHour === bucket.start && (
                  <Box sx={{ pl: 1 }}>
                    {hourLoading && hourEvents.length === 0 ? (
                      <CircularProgress size={18} sx={{ my: 1 }} />
                    ) : (
                      <Box component="pre" sx={{ ...LOG_BOX_SX, maxHeight: 360, minHeight: 80 }}>
                        {hourEvents.length
                          ? hourEvents
                              .map(
                                (e) =>
                                  `${e.timestamp ? formatTs(e.timestamp) + "  " : ""}${e.message}`,
                              )
                              .join("\n")
                          : "(no records in this hour)"}
                      </Box>
                    )}
                    {hourNextToken && (
                      <Button
                        size="small"
                        disabled={hourLoading}
                        onClick={() => loadHour(bucket, true)}
                      >
                        Load more
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Static (status / file) logs tab
// ---------------------------------------------------------------------------

function StaticLogsTab({
  kind,
  taskId,
}: {
  kind: "status" | "file";
  taskId: number;
}) {
  const [fetchStatus] = useLazyGetTaskStatusLogsQuery();
  const [fetchFile] = useLazyGetTaskFileLogsQuery();
  const [logs, setLogs] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res =
        kind === "file"
          ? await fetchFile(taskId).unwrap()
          : await fetchStatus(taskId).unwrap();
      setLogs(res.logs);
    } catch {
      setLogs("(failed to load logs)");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [taskId, kind]);

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2">
          {kind === "file" ? "File logs" : "Status logs"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} disabled={loading} onClick={load}>
          Refresh
        </Button>
      </Stack>
      <Box
        component="pre"
        sx={{
          ...LOG_BOX_SX,
          height: LOG_BOX_HEIGHT,
          ...(loading
            ? { display: "flex", alignItems: "center", justifyContent: "center" }
            : {}),
        }}
      >
        {loading ? <CircularProgress size={22} /> : logs || "(no records)"}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Task workspace (rendered inside Bots Management, in place of Major job workspace)
// ---------------------------------------------------------------------------

export type TaskWorkspaceProps = {
  taskId: number;
  userId: number;
  /** Return to the job / major-job view. */
  onBack: () => void;
  /** Jump to the connections screen for this user. */
  onManageConnections: () => void;
};

function TaskWorkspace({ taskId, userId, onBack, onManageConnections }: TaskWorkspaceProps) {
  const { data: task, isFetching, refetch } = useGetAdminTaskQuery(taskId, {
    skip: !taskId,
  });


  const [runTask] = useRunTaskMutation();
  const [stopTask] = useStopTaskMutation();
  const [setTaskPending] = useSetTaskPendingMutation();
  const [deleteTask] = useDeleteTaskMutation();
  const [updateTask] = useUpdateAdminTaskMutation();
  const [submit2FA] = useSubmitTask2FACodeMutation();

  const [tab, setTab] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [connOpen, setConnOpen] = useState(false);
  const [connSelected, setConnSelected] = useState<number | "">("");

  const { data: connData } = useGetBackendUserBotConnectionsQuery(userId, {
    skip: !userId || !connOpen,
  });

  const taskStatus = (task?.task_status as string) || "";
  const botStatus = (task?.bot_work_status as string) || "";
  const liveAvailable = Boolean(task?.live_logs_available);
  const isLocalRun = Boolean(task?.is_local_run);
  const isRunning = RUNNING_STATUSES.includes(taskStatus.toUpperCase());
  const taskTerminal = ["FINISHED", "STOPPED", "ERROR", "CRITICAL_ERROR"].includes(
    taskStatus.toUpperCase(),
  );

  // 2FA visibility mirrors the Major Job workspace: bot is waiting on 2FA AND a
  // pending/ready 2FA request exists. Poll while running so it surfaces even if
  // the WS push is unavailable.
  const { data: twoFaData, refetch: refetch2FA } = useGetTask2FAStatusQuery(taskId, {
    skip: !taskId,
    pollingInterval: isRunning ? 3000 : 0,
  });
  const twoFaStatus = (twoFaData?.status || "").toLowerCase();
  const show2FA =
    botStatus.toUpperCase() === "2FA" && ["pending", "ready"].includes(twoFaStatus);

  // Keep task data (bot status, ecs_task_id, counts) fresh while the bot runs.
  useEffect(() => {
    if (!isRunning) return undefined;
    const id = window.setInterval(() => {
      void refetch();
    }, 5000);
    return () => window.clearInterval(id);
  }, [isRunning, refetch]);

  // Close the 2FA dialog as soon as a code is no longer needed.
  useEffect(() => {
    if (!show2FA && twoFaOpen) setTwoFaOpen(false);
  }, [show2FA, twoFaOpen]);

  // Success/error counts computed from the task's status-log file (falls back to
  // live DB counters server-side when no log zip exists yet).
  const [fetchEmployeeStats] = useLazyGetTaskEmployeeStatsQuery();
  const [empStats, setEmpStats] = useState<{ success: number; error: number } | null>(
    null,
  );
  const reloadStats = () => {
    if (!taskId) return;
    fetchEmployeeStats(taskId)
      .unwrap()
      .then((r) => setEmpStats({ success: r.success, error: r.error }))
      .catch(() => undefined);
  };
  useEffect(() => {
    if (!taskId) return;
    fetchEmployeeStats(taskId)
      .unwrap()
      .then((r) => setEmpStats({ success: r.success, error: r.error }))
      .catch(() => undefined);
  }, [taskId, fetchEmployeeStats]);

  const run = async () => {
    setActionError(null);
    try {
      if (taskStatus.toUpperCase() !== "PENDING") await setTaskPending(taskId).unwrap();
      await runTask(taskId).unwrap();
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to run task."));
    }
  };

  const stop = async () => {
    setActionError(null);
    try {
      await stopTask(taskId).unwrap();
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to stop task."));
    }
  };

  const doDelete = async () => {
    setActionError(null);
    try {
      await deleteTask(taskId).unwrap();
      setDeleteOpen(false);
      onBack();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to delete task."));
      setDeleteOpen(false);
    }
  };

  const doSubmit2FA = async () => {
    setActionError(null);
    try {
      await submit2FA({ taskId, code: twoFaCode.trim() }).unwrap();
      setTwoFaOpen(false);
      setTwoFaCode("");
      // Poll until the bot consumes the code and the prompt clears.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const [, res] = await Promise.all([refetch(), refetch2FA()]);
        const fresh = (res.data?.status || "").toLowerCase();
        if (fresh !== "pending" && fresh !== "ready") break;
        await new Promise((resolve) => setTimeout(resolve, 700));
      }
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to submit 2FA code."));
    }
  };

  const doChangeConnection = async () => {
    if (connSelected === "") return;
    setActionError(null);
    try {
      await updateTask({ taskId, body: { connection: connSelected } }).unwrap();
      setConnOpen(false);
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to change connection."));
    }
  };

  const connections = connData?.connections ?? [];

  // --- Header labels (from TaskDetailSerializer) ---
  const userLabel = (task?.user_label as string) || "";
  const vendorLabel = (task?.connection_vendor as string) || "";

  // Connection pool for the picker: only logins of the same vendor as this task,
  // matching how the Major job workspace scopes its connection dropdown.
  const connPool = useMemo(() => {
    const v = vendorLabel.trim().toLowerCase();
    if (!v) return connections;
    return connections.filter(
      (c) => (c.vendor ?? "").trim().toLowerCase() === v,
    );
  }, [connections, vendorLabel]);
  const kindLabel = humanizeKind(task?.major_job_kind as string | undefined);
  const majorJobName = (task?.major_job_name as string) || "";
  const jobName = (task?.job_name as string) || "";
  const connUsername = (task?.connection_username as string) || "";
  const taskIndex = task?.task_index_in_job as number | null | undefined;
  const taskCount = task?.task_count_in_job as number | null | undefined;

  // Sequential task number within its job (fallback to DB id when not in a job).
  const taskTitle = taskIndex != null ? `Task ${taskIndex}` : `Task #${taskId}`;
  void taskCount;
  void userId;

  const goToConnections = onManageConnections;
  const ecsTaskId = (task?.ecs_task_id as string) || "";

  const [ecsCopied, setEcsCopied] = useState(false);
  const copyEcsTaskId = async () => {
    if (!ecsTaskId) return;
    try {
      await navigator.clipboard.writeText(ecsTaskId);
      setEcsCopied(true);
      window.setTimeout(() => setEcsCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Full task log history download (all_logs file). CloudWatch live logs only
  // cover one ECS task id and are lost on re-run; the all_logs file is the
  // complete history for this task.
  const [fetchAllLogs] = useLazyGetTaskAllLogsQuery();
  const [downloadingAll, setDownloadingAll] = useState(false);
  const downloadAllLogs = async () => {
    setActionError(null);
    setDownloadingAll(true);
    try {
      const res = await fetchAllLogs(taskId).unwrap();
      if (!res.found || !res.logs) {
        setActionError("No all-logs file found for this task yet.");
        return;
      }
      const blob = new Blob([res.logs], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `all_logs_task_${taskId}.log`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setActionError("Failed to download all-logs file.");
    } finally {
      setDownloadingAll(false);
    }
  };

  const infoItems = useMemo(
    () => [
      ["Bot", (task?.bot_name as string) || "—"],
      [
        "Employee range",
        task ? `${task.employee_number_from} — ${task.employee_number_to}` : "—",
      ],
      [
        "Success",
        String(empStats?.success ?? task?.successfully_processed_employees_count ?? 0),
      ],
      ["Errors", String(empStats?.error ?? task?.error_employees_count ?? 0)],
    ],
    [task, empStats],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Task workspace
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={onBack}
        >
          Back to job
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {taskTitle}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "flex-start",
                  mt: 0.75,
                }}
              >
                {userLabel && <StripItem title="User" value={userLabel} avatar />}
                {kindLabel && <StripItem title="Action" value={kindLabel} />}
                {vendorLabel && <StripItem title="Vendor" value={vendorLabel} />}
                {majorJobName && <StripItem title="Major job" value={majorJobName} />}
                {jobName && <StripItem title="Task job" value={jobName} />}
              </Box>
            </Box>
            <Tooltip title="Refresh task stats">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={isFetching}
                  onClick={() => {
                    void refetch();
                    reloadStats();
                  }}
                >
                  {isFetching ? (
                    <CircularProgress size={18} color="primary" />
                  ) : (
                    <ReplayIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Status: ${taskStatus || "—"}`}
              color={taskRunStatusChipColor(taskStatus)}
            />
            <Chip
              size="small"
              label={`Bot: ${botStatus || "—"}`}
              color={botWorkStatusChipColor(botStatus)}
            />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
              gap: 1.5,
              mb: 1.25,
            }}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ letterSpacing: 0.5, lineHeight: 1.4, fontWeight: 700 }}
              >
                Connection
              </Typography>
              <Tooltip title="Manage connections">
                <Typography
                  variant="body2"
                  onClick={goToConnections}
                  sx={{
                    mt: 0.25,
                    wordBreak: "break-all",
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {connUsername ||
                    (task?.connection != null ? `#${task.connection}` : "—")}
                </Typography>
              </Tooltip>
            </Box>
            {infoItems.map(([label, value]) => (
              <Box key={label}>
                <Typography
                  variant="overline"
                  color="text.primary"
                  sx={{ letterSpacing: 0.5, lineHeight: 1.4, fontWeight: 700 }}
                >
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all", mt: 0.25 }}>
                  {value}
                </Typography>
              </Box>
            ))}
            <Box>
              <Typography
                variant="overline"
                color="text.primary"
                sx={{ letterSpacing: 0.5, lineHeight: 1.4, fontWeight: 700 }}
              >
                ECS task id
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {ecsTaskId || "—"}
                </Typography>
                <Tooltip title={ecsCopied ? "Copied" : "Copy ECS task id"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={copyEcsTaskId}
                      disabled={!ecsTaskId}
                      aria-label="Copy ECS task id"
                    >
                      {ecsCopied ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ mb: 1.25 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {isRunning ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={stop}
            >
              Stop
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={run}
            >
              Run
            </Button>
          )}
          {show2FA && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<VpnKeyIcon />}
              onClick={() => setTwoFaOpen(true)}
            >
              Pass 2FA
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<SwapHorizIcon />}
            onClick={() => {
              setConnSelected((task?.connection as number) ?? "");
              setConnOpen(true);
            }}
          >
            Change connection
          </Button>
          <Tooltip title="Download the task's complete log history (all_logs file)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  downloadingAll ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={downloadingAll}
                onClick={downloadAllLogs}
              >
                Download all logs
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isRunning}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Live logs" />
          <Tab label="Status logs" />
          <Tab label="File logs" />
        </Tabs>
        {tab === 0 && (
          <LiveLogsTab
            taskId={taskId}
            liveAvailable={liveAvailable}
            isLocalRun={isLocalRun}
            taskTerminal={taskTerminal}
          />
        )}
        {tab === 1 && <StaticLogsTab kind="status" taskId={taskId} />}
        {tab === 2 && <StaticLogsTab kind="file" taskId={taskId} />}
      </Paper>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete {taskTitle.toLowerCase()}?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={doDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA dialog */}
      <Dialog open={twoFaOpen} onClose={() => setTwoFaOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Enter 2FA code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="2FA code"
            value={twoFaCode}
            onChange={(e) => setTwoFaCode(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTwoFaOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!twoFaCode.trim()} onClick={doSubmit2FA}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change connection dialog — same login labelling as the Major job workspace. */}
      <Dialog open={connOpen} onClose={() => setConnOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Change connection</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            size="small"
            label="Connection"
            margin="dense"
            value={connSelected}
            onChange={(e) => setConnSelected(Number(e.target.value))}
            sx={{
              "& .MuiSelect-select": {
                textAlign: "left",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            {connPool.map((c, ix) => (
              <MenuItem key={c.id} value={c.id}>
                {`${ix + 1}. ${c.username ? `@${c.username}` : "-"}`}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={connSelected === ""} onClick={doChangeConnection}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}

export default TaskWorkspace;
